import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { WBBox, WBFlex, WBTypography } from '@admiin-com/ds-web';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import { CurrencyNumber } from '../CurrencyNumber/CurrencyNumber';
import LineItemsMobile from './LineItemsMobile';
import { InvoiceCreateButtons } from '../InvoiceCreateForm/InvoiceCreateButtons';
import {
  Controller,
  useFormContext,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { Entity } from '@admiin-com/ds-graphql';
import { getAmountFromLineItems, getDefaultGST } from '../../helpers/tasks';
import LineItemInputTable from '../PayBill/BillLineItems/LineItemsTable';

interface Props {
  handleNext: () => void;
  onDraft: () => void;
  onPreview: () => void;
  isDraft?: boolean;
}
export default function InvoiceLineItems({
  handleNext,
  onDraft,
  onPreview,
  isDraft,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { control, setValue } = useFormContext();

  const { dirtyFields } = useFormState({
    control: control,
  });
  const isTouched = dirtyFields.lineItems;
  const rows = useWatch({ name: 'lineItems' });
  console.log(isTouched);

  const { subTotal, totalGST, total } = React.useMemo(
    () => getAmountFromLineItems(rows),
    [rows]
  );

  const onNext = () => {
    handleNext();
  };
  const { watch } = useFormContext();

  const from = watch('from') as Entity;

  const defaultGST = React.useMemo(() => getDefaultGST(from), [from]);

  const initialRow = React.useMemo(
    () => ({
      description: '',
      lineAmount: 0,
      unitAmount: 0,
      quantity: 1,
      taxType: defaultGST,
    }),
    [defaultGST]
  );

  React.useEffect(() => {
    if (defaultGST && from && rows.length === 0 && !isTouched) {
      setValue('lineItems', [initialRow], {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [rows, defaultGST, from, isTouched, setValue, initialRow]);

  return (
    <Container>
      <Content>
        <Controller
          name="lineItems"
          control={control}
          defaultValue={[]}
          render={({ field: { onChange, value } }) =>
            isMobile ? (
              <LineItemsMobile rows={value} setRows={onChange} />
            ) : (
              <LineItemInputTable
                setRows={onChange}
                rows={value}
                defaultGST={defaultGST}
                noGstInclusive
                hasServiceLookup
                fromEntityId={from?.id}
              />
            )
          }
        />
        {}
        <FooterContainer>
          <WBBox width={['100%', 300]}>
            <FooterRow>
              <WBTypography fontWeight={'bold'}>
                {t('subTotal', { ns: 'taskbox' })}
              </WBTypography>
              <CurrencyNumber
                number={subTotal}
                sup={false}
                fontWeight={'normal'}
              />
            </FooterRow>
            <FooterRow>
              <WBTypography fontWeight={'bold'}>
                {t('totalGST', { ns: 'taskbox' })}
              </WBTypography>
              <CurrencyNumber
                number={totalGST}
                sup={false}
                fontWeight={'normal'}
              />
            </FooterRow>
            <FooterRow>
              <WBTypography fontWeight={'bold'}>
                {t('total', { ns: 'taskbox' })}
              </WBTypography>
              <CurrencyNumber
                mb={0}
                variant="h3"
                number={total}
                sup={false}
                fontWeight={'bold'}
              />
            </FooterRow>
          </WBBox>
        </FooterContainer>
      </Content>
      <InvoiceCreateButtons
        onDraft={async () => await onDraft()}
        onPreview={async () => await onPreview()}
        handleNext={onNext}
        isValid={rows.length > 0}
        disableDraft={isDraft}
      />
    </Container>
  );
}
const Container = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  alignItems: 'end',
  backgroundColor: theme.palette.background.default,
}));

const Content = styled(WBBox)(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const FooterRow = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}));

const FooterContainer = styled(WBFlex)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.text.secondary}`,
  flexDirection: 'column',
  padding: theme.spacing(3, 2),
  width: '100%',
  alignItems: 'end',
  [theme.breakpoints.down('sm')]: {
    alignItems: 'start',
  },
}));
